import {
    PLAN_FIELD_KEYS,
    PLAN_KEYS,
    TIER_KEYS,
    sectionHasValues,
} from "./emptyProposal.js";
import { PLAN_LOGO_FIELDS, brandLogoHtml, planAdministratorBrand, virtualPrimaryCareBrand } from "./planBrandLogos.js";

const PLAN_LABELS = {
    centivoPartnershipPlan: "Centivo Partnership Plan",
    centivoHDHPPlan: "Centivo HDHP Plan",
    traditionalPPOPlan: "Traditional PPO Plan",
    traditionalHDHPPlan: "Traditional HDHP Plan",
};

const TIER_LABELS = {
    eeOnly: "EE only",
    eeSp: "EE + spouse",
    eeCh: "EE + child(ren)",
    family: "Family",
};

const PLAN_FIELD_LABELS = {
    "typicalAV(accounts for HRA/HSA)": "Typical AV (accounts for HRA/HSA)",
    planAdministrator: "Plan administrator",
    network: "Network",
    "oonEEOnly-ded/oopm/coins": "OON (EE only) ded / OOPM / coins",
    virtualPrimaryCare: "Virtual primary care",
    "pcp&ReferralsRequired": "PCP & referrals required",
    deductible: "Deductible",
    outOfPocketMax: "Out-of-pocket max",
    pcpVisits: "PCP visits",
    specialists: "Specialists",
    "basicLabs/imaging": "Basic labs / imaging",
    outpatientSurgery: "Outpatient surgery",
    inpatientSurgery: "Inpatient surgery",
    urgentCare: "Urgent care",
    emergencyRoom: "Emergency room",
    rx: "Rx",
};

const RATE_COLUMNS = [
    ["enrollment", "Enrollment"],
    ["fixedCost", "Fixed Costs"],
    ["medicalAccoont", "Medical Account"],
    ["billedMaxCount", "Billed<br>Max Costs"],
    ["premiumEquivalent", "Premium Equivalent"],
];

const RATE_TIER_LABELS = {
    eeOnly: "EE Only",
    eeSp: "EE+SP",
    eeCh: "EE+CH",
    family: "Family",
};

const LF_COLUMNS = [
    ["enrollment", "Projected<br>Enrollment", "count"],
    ["ratesAt100% (Expected)", "Rates at 100%<br>(Expected)", "money"],
    ["ratesAt110% (Mid-Low)", "Rates at 110%<br>(Mid-Low)", "money"],
    ["ratesAt115% (Mid-High)", "Rates at 115%<br>(Mid-High)", "money"],
    ["ratesAt115% (Max)", "Rates at 115%<br>(Max)", "money"],
];

const YEAR_COLUMNS = [
    ["renewal", "Renewal"],
    ["year2", "Year 2"],
    ["year3", "Year 3"],
    ["year4", "Year 4"],
    ["year5", "Year 5"],
];

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function display(value) {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return value == null ? "" : String(value);
}

function bind(parts) {
    if (!parts?.length) return "";
    return ` data-bind="${escapeHtml(JSON.stringify(parts))}"`;
}

function cell(value, className = "", parts) {
    const text = display(value);
    const savings = /^[(−-]/.test(text.trim()) ? " is-savings" : "";
    const cls = `${className}${savings}`.trim();
    return `<td${bind(parts)}${cls ? ` class="${cls}"` : ""}>${escapeHtml(text)}</td>`;
}

function moneyCell(value, parts) {
    const text = display(value).trim();
    const amount = text.match(/^\$\s*(.+)$/);
    if (!amount) return cell(text, "is-num", parts);
    return `<td class="is-money"${bind(parts)}><span class="is-dollar">$</span><span class="is-amount">${escapeHtml(amount[1].trim())}</span></td>`;
}

function lfValueCell(kind, value, parts) {
    if (kind === "money") return moneyCell(value, parts);
    return cell(value, kind === "count" ? "is-count" : "is-num", parts);
}

function logoMarkup(field, plan) {
    let value = plan?.[field];
    if (field === "planAdministrator" && !brandLogoHtml(value)) {
        const brand = planAdministratorBrand(plan);
        if (brand) value = brand;
    }
    if (field === "virtualPrimaryCare" && !brandLogoHtml(value)) {
        const brand = virtualPrimaryCareBrand(plan?.network);
        if (brand) value = brand;
    }
    return brandLogoHtml(value, field);
}

function planCell(field, plan, className, parts) {
    let value = plan?.[field];
    if (field === "planAdministrator" && !brandLogoHtml(value)) {
        const brand = planAdministratorBrand(plan);
        if (brand) value = brand;
    }
    if (field === "virtualPrimaryCare" && !brandLogoHtml(value)) {
        const brand = virtualPrimaryCareBrand(plan?.network);
        if (brand) value = brand;
    }
    if (PLAN_LOGO_FIELDS.has(field)) {
        const logo = brandLogoHtml(value, field);
        if (logo) return `<td class="${className}"${bind(parts)}>${logo}</td>`;
    }
    return cell(value, className, parts);
}

function frame(inner) {
    return `<div class="tpl-data is-excel-table"><div class="tpl-data-frame">${inner}</div></div>`;
}

function notesList(notes, prefix) {
    const items = (notes || [])
        .map((note, index) => ({ note: String(note).trim(), index }))
        .filter((item) => item.note);
    if (!items.length) return "";
    return `<ul class="tpl-data-notes">${items.map(({ note, index }) => `<li${bind(prefix ? [...prefix, index] : null)}>${escapeHtml(note)}</li>`).join("")}</ul>`;
}

function visiblePlanKeys(source) {
    const named = PLAN_KEYS.filter((plan) => source?.planNames?.[plan] || sectionHasValues(source?.[plan]));
    if (named.length) return named;
    return PLAN_KEYS.filter((plan) => plan !== "traditionalPPOPlan");
}

function planColumns(planDesign) {
    return visiblePlanKeys(planDesign);
}

function planIsAccent(planDesign, plan, index) {
    const marked = planDesign?.columnAccent;
    if (marked && Object.keys(marked).length) return Boolean(marked[plan]);
    const name = planDesign?.planNames?.[plan] || PLAN_LABELS[plan] || "";
    return index === 0 || /partnership/i.test(name);
}

function planDesignTable(planDesign) {
    const keys = planColumns(planDesign);
    const wide = keys.length > 3 ? " is-plans-wide" : "";
    const head = keys.map((plan, index) => {
        const name = planDesign?.planNames?.[plan] || PLAN_LABELS[plan];
        const accent = planIsAccent(planDesign, plan, index) ? "is-accent" : "";
        return `<th class="${accent}"${bind(["planDesign", "planNames", plan])}>${escapeHtml(name)}</th>`;
    }).join("");
    const body = PLAN_FIELD_KEYS.map((field) => {
        const av = field.startsWith("typicalAV") ? " class=\"is-av\"" : "";
        const values = keys.map((plan, index) => planCell(
            field,
            planDesign?.[plan],
            `is-plan${planIsAccent(planDesign, plan, index) ? " is-accent-col" : ""}`,
            ["planDesign", plan, field],
        )).join("");
        return `<tr${av}><th>${escapeHtml(PLAN_FIELD_LABELS[field] || field)}</th>${values}</tr>`;
    }).join("");
    return frame(`<table class="tpl-data-table tpl-plan-design${wide}"><thead><tr><th class="is-label"></th>${head}</tr></thead><tbody>${body}</tbody></table>`);
}

function ratesGrid(source, columns, options = {}) {
    const section = options.section || "rates";
    const keys = visiblePlanKeys(source);
    const head = `<th>Plan</th><th>Tier</th>${columns.map(([, label]) => `<th>${label}</th>`).join("")}`;
    const body = keys.map((plan) => {
        const name = source?.planNames?.[plan] || PLAN_LABELS[plan];
        return TIER_KEYS.map((tier, index) => {
            const planCell = index === 0
                ? `<th class="is-plan-name" rowspan="${TIER_KEYS.length}"${bind([section, "planNames", plan])}>${escapeHtml(name)}</th>`
                : "";
            const values = columns.map(([key]) => cell(source?.[plan]?.[tier]?.[key], "is-num", [section, plan, tier, key])).join("");
            return `<tr>${planCell}<td class="is-tier">${escapeHtml(RATE_TIER_LABELS[tier])}</td>${values}</tr>`;
        }).join("");
    }).join("");
    const total = source?.annualTotal || {};
    const totalCells = columns.map(([key]) => cell(total[key], "is-num", [section, "annualTotal", key])).join("");
    const totalLabel = options.totalLabel || "Annual Total";
    const totalRow = `<tr class="is-annual is-rates-foot"><th colspan="2">${escapeHtml(totalLabel)}</th>${totalCells}</tr>`;
    return frame(`<table class="tpl-data-table tpl-rates"><thead><tr>${head}</tr></thead><tbody>${body}${options.beforeTotal || ""}${totalRow}</tbody></table>`);
}

function writeWiseFooter(source, columns, section) {
    const rx = source.writeWiseRx || {};
    const monthly = source.monthlyTotal || {};
    const writeWiseCells = columns.map(([key]) => {
        if (key === "enrollment") return cell(rx.totalMembers, "is-num", [section, "writeWiseRx", "totalMembers"]);
        if (key === "billedMaxCount") return "<td>Included above</td>";
        return "<td></td>";
    }).join("");
    const monthlyCells = columns.map(([key]) => cell(monthly[key], "is-num", [section, "monthlyTotal", key])).join("");
    return `<tr class="is-writewise is-rates-foot"><th>WriteWise (Rx)</th><td class="is-tier">Total Members</td>${writeWiseCells}</tr><tr class="is-monthly is-rates-foot"><th colspan="2">Monthly Total (Fixed + Medical + Rx)</th>${monthlyCells}</tr>`;
}

function tierTables(plans, columns, section) {
    const keys = visiblePlanKeys(plans);
    return keys.map((plan) => {
        const head = columns.map(([, label]) => `<th>${escapeHtml(label)}</th>`).join("");
        const body = TIER_KEYS.map((tier) => {
            const values = columns.map(([key]) => cell(plans[plan]?.[tier]?.[key], "is-num", [section, plan, tier, key])).join("");
            return `<tr><td class="is-tier">${escapeHtml(TIER_LABELS[tier])}</td>${values}</tr>`;
        }).join("");
        const name = plans?.planNames?.[plan] || PLAN_LABELS[plan];
        return frame(`<table class="tpl-data-table"><thead><tr><th class="is-label" colspan="${columns.length + 1}"${bind([section, "planNames", plan])}>${escapeHtml(name)}</th></tr><tr><th class="is-label">Tier</th>${head}</tr></thead><tbody>${body}</tbody></table>`);
    }).join("");
}

function lfRateGrid(source) {
    const section = "lfRateTable";
    const keys = visiblePlanKeys(source);
    const banner = `<tr class="is-lf-banner"><th class="is-lf-blank" colspan="3"></th><th class="is-lf-title" colspan="${LF_COLUMNS.length - 1}">Centivo Illustrative Budget Rates</th></tr>`;
    const head = `<tr><th class="is-lf-id">Plan</th><th class="is-lf-id">Tier</th>${LF_COLUMNS.map(([, label, kind]) => `<th class="${kind === "money" ? "is-lf-rate" : "is-lf-id"}">${label}</th>`).join("")}</tr>`;
    const body = keys.map((plan, planIndex) => {
        const name = source?.planNames?.[plan] || PLAN_LABELS[plan];
        return TIER_KEYS.map((tier, index) => {
            const planCellHtml = index === 0
                ? `<th class="is-plan-name" rowspan="${TIER_KEYS.length}"${bind([section, "planNames", plan])}>${escapeHtml(name)}</th>`
                : "";
            const values = LF_COLUMNS.map(([key, , kind]) => lfValueCell(kind, source?.[plan]?.[tier]?.[key], [section, plan, tier, key])).join("");
            const start = planIndex > 0 && index === 0 ? ` class="is-plan-start"` : "";
            return `<tr${start}>${planCellHtml}<td class="is-tier">${escapeHtml(RATE_TIER_LABELS[tier])}</td>${values}</tr>`;
        }).join("");
    }).join("");
    const total = source?.annualTotal || {};
    const totalCells = LF_COLUMNS.map(([key, , kind]) => lfValueCell(kind, total[key], [section, "annualTotal", key])).join("");
    const totalRow = `<tr class="is-annual is-rates-foot"><th colspan="2">Annual Total</th>${totalCells}</tr>`;
    return frame(`<table class="tpl-data-table tpl-lf-rates"><thead>${banner}${head}</thead><tbody>${body}${totalRow}</tbody></table>`);
}

function ratesTable(data, variant) {
    if (variant === "lfRateTable") {
        const source = data.lfRateTable || {};
        const contract = source.stoplossContract || {};
        const contractRows = [
            ["Carrier", "carrier", contract.carrier],
            ["Contract type", "contractType", contract.contractType],
            ["Specific deductible", "specificDeductible", contract.specificDeductible],
            ["Aggregate corridor", "aggregateCorridor", contract.aggregateCorridor],
        ].filter(([, , value]) => display(value) || !sectionHasValues(contract));
        const contractTable = frame(`<table class="tpl-data-table"><thead><tr><th class="is-label" colspan="2">Stop-loss contract</th></tr></thead><tbody>${contractRows.map(([label, key, value]) => `<tr><th>${escapeHtml(label)}</th>${cell(value, "", ["lfRateTable", "stoplossContract", key])}</tr>`).join("")}</tbody></table>`);
        return `<div class="tpl-data-stack">${lfRateGrid(source)}${contractTable}${notesList(source.notes, ["lfRateTable", "notes"])}</div>`;
    }

    const source = (variant === "ratesWithWriteWise" ? data.ratesWithWriteWise : data.rates) || {};
    if (variant === "ratesWithWriteWise") {
        const columns = [
            ["enrollment", "Enrollment"],
            ["fixedCost", "Fixed Costs<br>(Admin + SL Premium)"],
            ["medicalAccoont", "Medical Claim<br>Account"],
            ["billedMaxCount", "Billed<br>Max Costs"],
        ];
        return `<div class="tpl-data-stack">${ratesGrid(source, columns, {
            section: "ratesWithWriteWise",
            totalLabel: "Annual Total (Fixed + Medical + Rx)",
            beforeTotal: writeWiseFooter(source, columns, "ratesWithWriteWise"),
        })}${notesList(source.notes, ["ratesWithWriteWise", "notes"])}</div>`;
    }
    return `<div class="tpl-data-stack">${ratesGrid(source, RATE_COLUMNS, { section: "rates" })}${notesList(source.notes, ["rates", "notes"])}</div>`;
}

function costTable(overview) {
    const spend = overview?.centivoClaimsAndSurplus?.totalSpend || {};
    const status = overview?.statusQuo?.totalSpend || {};
    const vs = overview?.centivoVsStatusQuo || {};
    const scenarios = [
        ["80%", "80%", spend["80%"], "is-sub-cv", ""],
        ["90%", "90%", spend["90%"], "is-sub-cv", ""],
        ["100%<br>(Expected)", "100% (Expected)", spend["100% (Expected)"], "is-sub-cv", "is-expected"],
        ["125%<br>(Billed Max)", "125% (Billed Max)", spend["125% (Billed Max)"], "is-sub-cv", ""],
    ];
    const num = (value, extra = "", parts) => cell(value, ["is-num", extra].filter(Boolean).join(" "), parts);
    const four = (key) => {
        const values = vs[key];
        const list = Array.isArray(values) ? values : [];
        return [0, 1, 2, 3].map((index) => list[index] ?? "");
    };
    const spendPath = ["costComparisionOverview", "centivoClaimsAndSurplus", "totalSpend"];
    const statusPath = ["costComparisionOverview", "statusQuo", "totalSpend"];
    const cols = `<colgroup><col class="is-label" /><col /><col /><col /><col /><col /><col /></colgroup>`;
    const spendTable = `<table class="tpl-data-table tpl-cost-compare">${cols}<thead><tr><th class="is-corner" rowspan="2"></th><th colspan="2">Status Quo</th><th class="is-accent" colspan="${scenarios.length}">Centivo Claims and Surplus</th></tr><tr><th class="is-sub-sq">Current</th><th class="is-sub-sq">Renewal: 30%</th>${scenarios.map(([label, , , cls]) => `<th class="${cls}">${label}</th>`).join("")}</tr></thead><tbody><tr class="is-spend"><th>Total Spend</th>${num(status.current, "", [...statusPath, "current"])}${num(status["renewal30%"], "", [...statusPath, "renewal30%"])}${scenarios.map(([, key, value, , extra]) => num(value, extra, [...spendPath, key])).join("")}</tr></tbody></table>`;
    const vsRows = [
        ["Vs. Current ($)", "vsCurrent($)"],
        ["Vs. Current (%)", "vsCurrent(%)"],
        ["Vs. Renewal ($)", "vsRenewal($)"],
        ["Vs. Renewal (%)", "vsRenewal(%)"],
    ].map(([label, key]) => `<tr><th class="is-vs-label" colspan="3">${escapeHtml(label)}</th>${four(key).map((value, index) => num(value, index === 2 ? "is-expected" : "", ["costComparisionOverview", "centivoVsStatusQuo", key, index])).join("")}</tr>`).join("");
    const vsTable = `<table class="tpl-data-table tpl-cost-compare">${cols}<thead><tr><th class="is-blank" colspan="3"></th><th class="is-accent" colspan="4">Centivo vs Status Quo</th></tr></thead><tbody>${vsRows}</tbody></table>`;
    return `<div class="tpl-data-stack">${frame(spendTable)}${frame(vsTable)}${notesList(overview?.notes, ["costComparisionOverview", "notes"])}</div>`;
}

function stopLossTable(stoploss = {}) {
    const rows = [
        ["Carrier", "carrier", stoploss.carrier],
        ["Effective date", "effectiveDate", stoploss.effectiveDate],
        ["Contract", "contract", stoploss.contract],
        ["Deductible", "deductible", stoploss.deductible],
        ["Broker compensation", "brokerComp", stoploss.brokerComp],
        ["Proposal expiration", "proposalExpiration", stoploss.proposalExpiration],
    ];
    return frame(`<table class="tpl-data-table"><tbody>${rows.map(([label, key, value]) => `<tr><th>${escapeHtml(label)}</th>${cell(value, "", ["stoploss", key])}</tr>`).join("")}</tbody></table>`);
}

const YEAR_ROWS = [
    ["fullyInsured", "Fully Insured", "is-fi"],
    ["centivoMaxCosts", "Centivo Max Costs", "is-max"],
    ["centivoExpectedCost", "Centivo Expected Costs", "is-exp"],
];

const YEAR_CARDS = [
    ["totalExpected5YearsSavings", "Total Expected 5-Year Savings", "is-total"],
    ["employerExpected5YearSavings", "Employer Expected 5-Year Savings", "is-employer"],
    ["employeeExpected5YearsSaving", "Employee Expected 5-Year Savings", "is-employee"],
];

function parseMoney(value) {
    const text = String(value ?? "").replace(/[$,\s]/g, "");
    if (!text || text === "-") return null;
    const amount = Number(text);
    return Number.isFinite(amount) ? amount : null;
}

function formatAxis(amount) {
    return `$${Math.round(amount).toLocaleString("en-US")}`;
}

function fiveYearChart(saving) {
    const series = [
        ["fullyInsured", "Fully Insured", "#f15d2a"],
        ["centivoMaxCosts", "Centivo Max Costs", "#cac1e6"],
        ["centivoExpectedCost", "Centivo Expected Costs", "#486f7e"],
    ];
    const amounts = series.map(([key]) => YEAR_COLUMNS.map(([field]) => parseMoney(saving?.[key]?.[field]) || 0));
    const peak = Math.max(0, ...amounts.flat());
    const top = peak > 0 ? peak * 1.08 : 1;
    const ticks = 5;
    const width = 640;
    const height = 300;
    const left = 78;
    const right = 16;
    const plotTop = 36;
    const plotBottom = 228;
    const plotWidth = width - left - right;
    const plotHeight = plotBottom - plotTop;
    const group = plotWidth / YEAR_COLUMNS.length;
    const bar = 16;
    const bars = amounts.map((row, seriesIndex) => row.map((amount, yearIndex) => {
        const h = peak > 0 ? (amount / top) * plotHeight : 0;
        const x = left + yearIndex * group + (group - bar * 3) / 2 + seriesIndex * bar;
        const y = plotBottom - h;
        return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bar - 2}" height="${h.toFixed(1)}" fill="${series[seriesIndex][2]}" rx="1" />`;
    }).join("")).join("");
    const grid = Array.from({ length: ticks + 1 }, (_, index) => {
        const value = (top * (ticks - index)) / ticks;
        const y = plotTop + (plotHeight * index) / ticks;
        const label = peak > 0 ? formatAxis(value) : (index === ticks ? "$0" : "");
        return `<line x1="${left}" y1="${y}" x2="${width - right}" y2="${y}" stroke="#d5ddda" stroke-width="1" />
            <text x="${left - 8}" y="${y + 4}" text-anchor="end" fill="#406471" font-size="11">${escapeHtml(label)}</text>`;
    }).join("");
    const labels = YEAR_COLUMNS.map((_, index) => {
        const x = left + index * group + group / 2;
        return `<text x="${x}" y="${plotBottom + 18}" text-anchor="middle" fill="#406471" font-size="12">${index + 1}</text>`;
    }).join("");
    const legend = series.map(([, label, color], index) => {
        const x = 150 + index * 160;
        return `<rect x="${x}" y="268" width="12" height="12" fill="${color}" /><text x="${x + 18}" y="278" fill="#406471" font-size="11">${escapeHtml(label)}</text>`;
    }).join("");
    return `<div class="tpl-five-chart"><svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Healthcare Costs - 5 Year Trend">
        <text x="${width / 2}" y="20" text-anchor="middle" fill="#406471" font-size="15" font-weight="700">Healthcare Costs - 5 Year Trend</text>
        ${grid}${bars}${labels}${legend}
    </svg></div>`;
}

function fiveYearTable(saving) {
    const head = YEAR_COLUMNS.map(([, label]) => `<th>${escapeHtml(label)}</th>`).join("");
    const body = YEAR_ROWS.map(([key, label, rowClass]) => {
        const values = YEAR_COLUMNS.map(([field]) => cell(saving?.[key]?.[field], "is-num", ["totalCostOfCareSaving", key, field])).join("");
        return `<tr class="${rowClass}"><th>${escapeHtml(label)}</th>${values}</tr>`;
    }).join("");
    const cards = YEAR_CARDS.map(([key, label, cardClass]) => (
        `<div class="tpl-five-card ${cardClass}"><strong${bind(["totalCostOfCareSaving", key])}>${escapeHtml(display(saving?.[key]))}</strong><span>${escapeHtml(label)}</span></div>`
    )).join("");
    return `<div class="tpl-data-stack">${frame(`<table class="tpl-data-table tpl-five"><thead><tr><th>Scenario</th>${head}</tr></thead><tbody>${body}</tbody></table>`)}<div class="tpl-five-cards">${cards}</div>${fiveYearChart(saving)}</div>`;
}

const ASO_TIERS = [
    ["eeOnly", "EEO"],
    ["eeSp", "EES/EE+1"],
    ["eeCh", "EEC"],
    ["family", "FAM/EE+2 or more"],
    ["compositePEPM", "Composite PEPM", true],
];

function asoAt(root, path) {
    return path.reduce((node, key) => (node && typeof node === "object" ? node[key] : undefined), root);
}

function asoRate(node) {
    if (!node || typeof node !== "object") return display(node);
    return display(node.centivoAsoProposal2027Rates ?? node.centivoAsoProposal ?? "");
}

function asoBlank() {
    return `${cell("")}${cell("")}${cell("")}${cell("")}`;
}

function asoFeeCells(node, path = []) {
    const hasSpread = node && (node.currentFI || node.renewalFI);
    const rateKey = node && Object.prototype.hasOwnProperty.call(node, "centivoAsoProposal2027Rates")
        ? "centivoAsoProposal2027Rates"
        : "centivoAsoProposal";
    return [
        cell(hasSpread ? "" : node?.enrolledEmployee, "is-num", [...path, "enrolledEmployee"]),
        cell(node?.currentFI, "is-num", [...path, "currentFI"]),
        cell(node?.renewalFI, "is-num", [...path, "renewalFI"]),
        cell(asoRate(node), "is-num", [...path, rateKey]),
    ].join("");
}

function asoVsCells(node) {
    const parts = asoRate(node).split("|").map((part) => part.trim());
    return `${cell("")}${cell("")}${cell(parts[0] || "", "is-num")}${cell(parts[1] || "", "is-num")}`;
}

function asoTierRows(section) {
    return [
        ...ASO_TIERS.map(([key, label, strong]) => ({ label, path: [section, key], strong })),
    ];
}

const ASO_ROWS = [
    { section: "Fixed costs" },
    { label: "Centivo Admin Fee", path: ["fixedCost", "centivoAdminFee"] },
    { label: "Network Access Fee", group: true },
    { label: "High Performance Fee", path: ["fixedCost", "networkAccessFee", "highProformanceFee"], indent: true },
    { label: "PBM", path: ["fixedCost", "networkAccessFee", "pbm"] },
    { label: "Cost Containment Fees", group: true },
    { label: "Archimedes Rx", path: ["fixedCost", "costContainmentFees", "archimedesRx"], indent: true },
    { label: "Compliance", path: ["fixedCost", "costContainmentFees", "compliance"], indent: true },
    { label: "Rx Rebate Projection", path: ["fixedCost", "costContainmentFees", "rxRebateProjection"] },
    { label: "Broker and GA Comp", path: ["fixedCost", "brokerAndGAComp"], strong: true },
    { label: "PEPM Admin Fee", path: ["fixedCost", "pepmAdminFee"], strong: true },
    { label: "Total Annual Admin Fees", path: ["fixedCost", "totalAnualAdminFee"], strong: true },
    { section: "Stop loss terms" },
    { label: "Stop Loss Carrier", path: ["stopLossTerms", "stopLossCarrier"] },
    { label: "Spec Deductible", path: ["stopLossTerms", "specDeductible"] },
    { label: "Contract Type", path: ["stopLossTerms", "contractType"] },
    { label: "Agg Corridor", path: ["stopLossTerms", "aggCorridor"] },
    { label: "Status", path: ["stopLossTerms", "status"] },
    { label: "Expiration Date", path: ["stopLossTerms", "expirationDate"] },
    { section: "Laser" },
    { label: "Lasers (Yes or No)", path: ["laser", "lasers"] },
    { label: "Lasered Claimants", path: ["laser", "laseredClaimants"] },
    { label: "Total Laser Liability", path: ["laser", "totlaLaserLiability"] },
    { section: "Spec stop loss premiums" },
    ...asoTierRows("specStopLossPremiums"),
    { label: "Total Annualized Premium", path: ["specStopLossPremiums", "totalAnnualizedPremium"], strong: true },
    { section: "Aggregate stop loss" },
    { label: "Aggregate + MGA Premium Rate", path: ["aggregateStopLoss", "aggregate+mgaPremiumRate"] },
    { label: "Total Annualized Premium", path: ["aggregateStopLoss", "totalAnnualizedPremium"], strong: true },
    { section: "Aggregate stop loss factors" },
    ...asoTierRows("aggregateStopLossFactors"),
    { label: "Total Annualized Agg Liability", path: ["aggregateStopLossFactors", "totalAnnualizedAggLiability"], strong: true },
    { section: "Projected claims cost" },
    ...asoTierRows("projectedClaimsCost"),
    { label: "Total Annualized Projected Liability", path: ["projectedClaimsCost", "totalAnnualizedProjectedLiability"], strong: true },
    { label: "Projected Total Liability", path: ["projectedTotalLiability"], banner: true },
    { label: "vs Current", path: ["projectedTotalLiability", "vsCurrent"], vs: true },
    { label: "vs Renewal", path: ["projectedTotalLiability", "vsRenewal"], vs: true },
    { label: "Max Total Liability", path: ["maxTotalLiability"], banner: true },
    { label: "vs Current", path: ["maxTotalLiability", "vsCurrent"], vs: true },
    { label: "vs Renewal", path: ["maxTotalLiability", "vsRenewal"], vs: true },
];

function asoTable(aso) {
    const source = aso || {};
    const body = ASO_ROWS.map((row) => {
        if (row.section) {
            return `<tr class="is-aso-section"><th colspan="5">${escapeHtml(row.section)}</th></tr>`;
        }
        const node = row.path ? asoAt(source, row.path) : undefined;
        const classes = [
            row.group ? "is-aso-group" : "",
            row.banner ? "is-banner" : "",
            row.vs ? "is-vs" : "",
            row.strong ? "is-strong" : "",
            row.indent ? "is-indent" : "",
        ].filter(Boolean).join(" ");
        const values = row.group ? asoBlank() : row.vs ? asoVsCells(node) : asoFeeCells(node, ["asoOption", ...(row.path || [])]);
        return `<tr${classes ? ` class="${classes}"` : ""}><th>${escapeHtml(row.label)}</th>${values}</tr>`;
    }).join("");
    const head = `<th class="is-blank"></th><th>Enrolled<br>Employees</th><th>Current FI</th><th>Renewal FI</th><th class="is-accent">Centivo ASO<br>Proposal:<br>2027 Rates</th>`;
    return `<div class="tpl-data-stack">${frame(`<table class="tpl-data-table tpl-aso-grid"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`)}${notesList(source.notes, ["asoOption", "notes"])}</div>`;
}

export function patchPreviewValue(doc, parts, data) {
    if (!doc || !parts?.length) return false;
    const key = JSON.stringify(parts);
    const nodes = [...doc.querySelectorAll("[data-bind]")].filter((node) => node.getAttribute("data-bind") === key);
    if (!nodes.length) return false;
    const value = parts.reduce((node, part) => (node != null && typeof node === "object" ? node[part] : undefined), data);
    const text = display(value);
    const logoField = parts[0] === "planDesign" && PLAN_LOGO_FIELDS.has(String(parts[2] ?? ""));
    nodes.forEach((node) => {
        if (logoField && node.tagName === "TD") {
            const markup = logoMarkup(parts[2], data?.planDesign?.[parts[1]]);
            if (markup) node.innerHTML = markup;
            else node.textContent = text;
            return;
        }
        const money = node.classList.contains("is-money") ? text.trim().match(/^\$\s*(.+)$/) : null;
        if (money) {
            node.innerHTML = `<span class="is-dollar">$</span><span class="is-amount">${escapeHtml(money[1].trim())}</span>`;
        } else {
            node.textContent = text;
        }
        if (node.tagName === "TD") node.classList.toggle("is-savings", /^[(−-]/.test(text.trim()));
    });
    if (parts[0] === "totalCostOfCareSaving" && YEAR_ROWS.some(([row]) => row === parts[1])) {
        const chart = doc.querySelector(".tpl-five-chart");
        if (chart) chart.outerHTML = fiveYearChart(data?.totalCostOfCareSaving);
    }
    return true;
}

export function buildLevelFundedTables(data, ratesTableVariant = "rates") {
    const proposal = data || {};
    return {
        planDesign: planDesignTable(proposal.planDesign),
        costComparison: costTable(proposal.costComparisionOverview),
        rates: ratesTable(proposal, ratesTableVariant),
        stopLoss: stopLossTable(proposal.stoploss),
        asoOption: asoTable(proposal.asoOption),
        fiveYear: fiveYearTable(proposal.totalCostOfCareSaving),
    };
}
